import React, { useState } from "react";
import "./IndustrySection.css";
import { motion, AnimatePresence } from 'motion/react';
import { 
  GraduationCap, 
  Home, 
  Heart, 
  ShoppingBag, 
  Hotel, 
  Plane,
  Sparkles,
  ArrowRight,
  Bot,
  User as UserIcon
} from 'lucide-react';const useCases = [
  {
    id: 'education',
    icon: GraduationCap,
    title: 'Education',
    subtitle: 'Virtual Campus Copilot',
    blurb: 'Guide students through programs, applications, fees, and campus resources with verifiable answers from your handbooks and site.',
    grad: 'linear-gradient(135deg, #a855f7, #7c3aed)',
    bgGrad: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
    messages: [
      { who: 'user', text: 'Do you offer BSc programs with AI modules?' },
      { who: 'bot', text: 'Yes — our BSc in Software Engineering includes AI/ML modules and a capstone.' },
      { who: 'user', text: "What's the yearly tuition?" },
      { who: 'bot', text: 'Tuition is approx. $40K/yr. Many students qualify for merit-based scholarships.' },
    ],
  },
  {
    id: 'real-estate',
    icon: Home,
    title: 'Real Estate',
    subtitle: 'AI Property Assistant',
    blurb: 'Instant property facts, viewing schedules, and neighborhood insights synced from your MLS/CRM.',
    grad: 'linear-gradient(135deg, #22c55e, #10b981)',
    bgGrad: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    messages: [
      { who: 'user', text: '2BHK near JP Nagar, pet friendly, parking?' },
      { who: 'bot', text: 'Found 4 options within 3 km. The closest is Sunrise Heights — 2BHK, pets OK, covered parking.' },
      { who: 'user', text: 'Book a visit for Friday 5 pm.' },
      { who: 'bot', text: 'Done. Calendar invite sent and agent Priya assigned.' },
    ],
  },
  {
    id: 'healthcare',
    icon: Heart,
    title: 'Healthcare',
    subtitle: 'Virtual Health Advisor',
    blurb: 'Triage FAQs, insurance coverage, and appointment routing with safe escalations to staff.',
    grad: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
    bgGrad: 'linear-gradient(135deg, #eff6ff, #cffafe)',
    messages: [
      { who: 'user', text: 'Is physiotherapy covered by HMO Silver?' },
      { who: 'bot', text: 'Yes, 8 sessions/year with ₹500 copay per visit. Need me to check next availability?' },
      { who: 'user', text: 'Yes, next week.' },
      { who: 'bot', text: 'Booked Tue 10:30 AM with Dr. Rao. Reminder set.' },
    ],
  },
  {
    id: 'retail',
    icon: ShoppingBag,
    title: 'Retail',
    subtitle: 'AI Shopping Assistant',
    blurb: 'Personalized product discovery, bundling, and post-purchase support connected to inventory.',
    grad: 'linear-gradient(135deg, #f97316, #ef4444)',
    bgGrad: 'linear-gradient(135deg, #fff7ed, #fee2e2)',
    messages: [
      { who: 'user', text: 'Need a breathable cotton tee in grey, size M.' },
      { who: 'bot', text: 'Here are 3 breathable tees in M. The EcoSoft Grey ships today. Want to add to cart?' },
      { who: 'user', text: 'Add 2 pieces and apply SUMMER10.' },
      { who: 'bot', text: 'Added. Code applied — you saved ₹240.' },
    ],
  },
  {
    id: 'hospitality',
    icon: Hotel,
    title: 'Hospitality',
    subtitle: 'Concierge AI Copilot',
    blurb: '24×7 concierge for amenities, upgrades, late check-outs, and local recommendations.',
    grad: 'linear-gradient(135deg, #14b8a6, #06b6d4)',
    bgGrad: 'linear-gradient(135deg, #f0fdfa, #cffafe)',
    messages: [
      { who: 'user', text: 'Any late checkout available on Sunday?' },
      { who: 'bot', text: 'Yes, 2 PM late checkout is complimentary for Gold members. Shall I confirm?' },
      { who: 'user', text: 'Please do. Also, best vegetarian brunch nearby?' },
      { who: 'bot', text: "Confirmed. 'Green Fork' is 600 m away, 4.6★ rating." },
    ],
  },
  {
    id: 'travel',
    icon: Plane,
    title: 'Travel',
    subtitle: 'Interactive Trip Planner',
    blurb: 'Plans adapt to delays and preferences with live updates across flights, hotels, and transfers.',
    grad: 'linear-gradient(135deg, #ec4899, #f43f5e)',
    bgGrad: 'linear-gradient(135deg, #fdf2f8, #ffe4e6)',
    messages: [
      { who: 'user', text: 'Rain expected in Phuket — reschedule island tour?' },
      { who: 'bot', text: 'Rescheduled to Thursday (clear skies). Rebooked cab and nudged guides — all set.' },
      { who: 'user', text: 'Great, send updated plan.' },
      { who: 'bot', text: 'Itinerary updated and shared to WhatsApp + Email.' },
    ],
  },
];



const IndustrySection = () => {
   const [activeIndex, setActiveIndex] = useState(0);
  const activeCase = useCases[activeIndex];
  const ActiveIcon = activeCase.icon;

  return (
    <section className="industry-section">
      <div className="industry-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="industry-header"
        >
          <div className="industry-badge">
            <Sparkles className="badge-icon" />
            <span>Built to fit your workflow</span>
          </div>

          <h2 className="industry-title">
            Where teams use <span className="brand-text">XenaChat</span> today
          </h2>

          <p className="industry-subtitle">
            Tailored AI assistants for every industry, trained on your data
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="industry-grid">
          {/* Left: Industry Tabs */}
          <div className="industry-tabs">
          {useCases.map((useCase, index) => {
  const Icon = useCase.icon;

  return (
    <motion.button
                key={useCase.id}
                onClick={() => setActiveIndex(index)}
                className={`industry-tab ${activeIndex === index ? 'active' : ''}`}
                style={{ '--grad': useCase.grad }}
              >
                <AnimatePresence initial={false}>
                    {activeIndex === index && (
                      <motion.span
                        className="tab-shade"
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{ scaleX: 1, opacity: 0.14 }}
                        exit={{ scaleX: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>

                <div className="tab-icon">
                  <Icon className="icon" />
                </div>

                <div className="tab-content">
                  <div className="tab-title">{useCase.title}</div>
                  <div className="tab-subtitle">{useCase.subtitle}</div>
                </div>

                {activeIndex === index && (
                  <motion.div className="tab-active-dot" />
                )}
              </motion.button>
            );
          })}
          </div>

          {/* Right: Chat Preview */}
          <div className="industry-preview">
            <AnimatePresence mode="wait">
             <motion.div
                key={activeIndex}
                className="preview-card"
                style={{ '--grad': activeCase.grad, '--bg': activeCase.bgGrad }}
              >
                {/* Card Header */}
                 <div className="preview-header">
                  <div className="preview-header-content">
                    <div className="preview-icon">
                      <ActiveIcon className="icon" />
                    </div>
                    <div className="preview-header-text">
                      <div className="preview-title">{activeCase.title} Copilot</div>
                      <div className="preview-status">
                        <span className="status-dot"></span>
                        <span>Online</span>
                      </div>
                    </div>
                  </div>
                </div>


                        <div className="preview-chat">
                {activeCase.messages.map((msg, idx) => (
                  <div key={idx} className={`chat-message-industry ${msg.who}`}>
                    <div className="message-wrapper">
                      {msg.who === 'bot' && (
                        <div className="message-avatar-industry bot-avatar">
                          <Bot className="avatar-icon" />
                        </div>
                      )}

                      <div className={`message-bubble-industry ${msg.who}`}>
                        {msg.text}
                      </div>

                      {msg.who === 'user' && (
                        <div className="message-avatar-industry user-avatar">
                          <UserIcon className="avatar-icon" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

                {/* Quick Stats */}
                <div className="preview-stats">
                  <div className="stat-item">
                    <div className="stat-value">95%</div>
                    <div className="stat-label">Accuracy</div>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <div className="stat-value">&lt;2s</div>
                    <div className="stat-label">Response</div>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <div className="stat-value">24/7</div>
                    <div className="stat-label">Available</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="industry-cta"
        >
         
        </motion.div>
      </div>
    </section>
  );
};

export default IndustrySection;
